/**
 * Level20Scene - 关卡20：宇宙创生
 * @description 终局场景 - 从黑洞内部"创生"新宇宙，综合运用所有物理概念
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import { Blackhole } from '@/physics/Blackhole.ts';
import { Relativity, SPEED_OF_LIGHT } from '@/physics/Relativity.ts';
import type { LevelConfig, CheckpointConfig, StageConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

type GameState = 'idle' | 'running' | 'transitioning' | 'finale' | 'success' | 'failed';
type CurrentStage = 1 | 2 | 3 | 4 | 5;

// 各阶段主题颜色 - 从黑暗到光明
const STAGE_COLORS: Record<CurrentStage, number> = {
  1: 0x1a0a2e, // 深紫黑 - 黑洞引力
  2: 0x2a1a4e, // 暗紫 - 事件视界
  3: 0x4a2a6e, // 紫 - 奇点
  4: 0xff6b35, // 橙红 - 大爆炸
  5: 0x4ade80, // 绿色 - 新宇宙
};

const CHECKPOINT_COLOR = 0xffa500;
const GOAL_COLOR = 0x4ade80;

// 粒子系统配置
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: number;
  size: number;
}

// 星星配置
interface Star {
  x: number;
  y: number;
  brightness: number;
  size: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

// 大爆炸阶段
type BigBangPhase = 'preparation' | 'ignition' | 'expansion' | 'cooling' | 'complete';

export class Level20Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private blackhole!: Blackhole;
  private relativity!: Relativity;
  private levelConfig: LevelConfig | null = null;
  private gameState: GameState = 'idle';
  private currentStage: CurrentStage = 1;
  private checkpoints: CheckpointConfig[] = [];
  private stages: StageConfig[] = [];
  private completedStages: Set<number> = new Set();
  private checkpointGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private goalZone!: Phaser.Geom.Rectangle;
  private isVictory: boolean = false;

  // UI 文本元素
  private stageText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private formulaText!: Phaser.GameObjects.Text;
  private velocityText!: Phaser.GameObjects.Text;
  private gammaText!: Phaser.GameObjects.Text;
  private timeDisplayText!: Phaser.GameObjects.Text;

  // 时间追踪
  private elapsedTime: number = 0;
  private totalGameTime: number = 0;

  // 玩家速度相关
  private playerVelocityRatio: number = 0.0;
  private playerGamma: number = 1.0;
  private baseWalkSpeed: number = 8;

  // 黑洞效果
  private blackholeGraphics!: Phaser.GameObjects.Graphics;
  private blackholePosition: Vec2 = Vec2(0, 0);
  private blackholeMass: number = 2000;
  private blackholeRadius: number = 100;
  private eventHorizonPulse: number = 0;

  // 粒子系统
  private particles: Particle[] = [];
  private particleGraphics!: Phaser.GameObjects.Graphics;

  // 星星背景
  private stars: Star[] = [];
  private starGraphics!: Phaser.GameObjects.Graphics;

  // 大爆炸效果
  private bigBangPhase: BigBangPhase = 'preparation';
  private bigBangProgress: number = 0;
  private bigBangGraphics!: Phaser.GameObjects.Graphics;
  private expansionRadius: number = 0;
  private universeCreated: boolean = false;

  // 成就系统
  private achievements: Array<{
    id: string;
    name: string;
    description: string;
    unlocked: boolean;
  }> = [];

  // 游戏统计
  private gameStats = {
    totalLevelsCompleted: 0,
    totalPlayTime: 0,
    conceptsLearned: 0,
    achievementsUnlocked: 0,
  };

  constructor() {
    super({ key: 'Level20Scene' });
  }

  preload(): void {
    this.load.json('level-20', 'src/levels/levels/level-20.json');
  }

  create(): void {
    this.levelConfig = this.cache.json.get('level-20') as LevelConfig;

    if (this.levelConfig) {
      this.checkpoints = this.levelConfig.checkpoints ?? [];
      this.stages = this.levelConfig.stages ?? [];
    }

    // 初始化物理世界（极低重力 - 黑洞环境）
    const stagePhysics = this.stages.find(s => s.stage === 1)?.physics;
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(
        stagePhysics?.gravity?.x ?? this.levelConfig?.physics.gravity.x ?? 0,
        stagePhysics?.gravity?.y ?? this.levelConfig?.physics.gravity.y ?? -2
      ),
      allowSleep: this.levelConfig?.physics.allowSleep ?? true,
    });

    // 初始化黑洞
    this.blackholePosition = Vec2(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.blackhole = new Blackhole({
      id: 'main-blackhole',
      position: this.blackholePosition,
      mass: this.blackholeMass,
    });
    this.blackholeRadius = this.blackhole.getSchwarzschildRadius();

    // 初始化相对论计算器
    this.relativity = new Relativity(SPEED_OF_LIGHT);

    // 效果图形层
    this.blackholeGraphics = this.add.graphics();
    this.blackholeGraphics.setDepth(5);

    this.particleGraphics = this.add.graphics();
    this.particleGraphics.setDepth(6);

    this.starGraphics = this.add.graphics();
    this.starGraphics.setDepth(0);

    this.bigBangGraphics = this.add.graphics();
    this.bigBangGraphics.setDepth(10);

    // 创建场景元素
    this.createStarBackground();
    this.createBoundaries();
    this.createPlatforms();
    this.createCheckpoints();
    this.createGoalZone();
    this.createStickman();
    this.createUI();
    this.createAchievements();
    this.setupInput();

    // 初始化状态
    this.gameState = 'running';
    this.currentStage = 1;
    this.completedStages = new Set();
    this.isVictory = false;
    this.elapsedTime = 0;
    this.playerVelocityRatio = 0.0;
    this.playerGamma = 1.0;
    this.bigBangPhase = 'preparation';
    this.bigBangProgress = 0;
    this.universeCreated = false;

    // 加载游戏统计
    this.loadGameStats();

    // 启动视觉效果更新
    this.startVisualEffects();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState === 'success' || this.gameState === 'failed') return;

    const dt = delta / 1000;
    this.elapsedTime += dt;
    this.totalGameTime += dt;

    if (this.gameState === 'running') {
      // 物理步进
      this.physicsWorld.step(dt);

      // 更新相对论参数
      this.updateRelativityParameters();

      // 更新黑洞引力效果
      this.updateBlackholeGravity();

      // 更新火柴人渲染
      this.stickman.update();

      // 检查检查点碰撞
      this.checkCheckpointCollisions();

      // 检查胜利条件
      this.checkVictory();
    } else if (this.gameState === 'finale') {
      // 大爆炸动画
      this.updateBigBangAnimation(dt);
    }

    // 更新阶段显示
    this.updateStageDisplay();

    // 更新视觉效果
    this.updateVisualEffects(dt);
  }

  /**
   * 更新黑洞引力效果
   */
  private updateBlackholeGravity(): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (!torso) return;

    const torsoPos = torso.getPosition();
    const playerPos = Vec2(torsoPos.x, GAME_HEIGHT - torsoPos.y);

    // 计算到黑洞的距离
    const distance = this.blackhole.calculateDistance(playerPos);

    // 如果在黑洞影响范围内，应用引力
    if (distance < this.blackholeRadius * 10 && distance > this.blackholeRadius) {
      const acceleration = this.blackhole.calculateGravitationalAcceleration(playerPos);

      // 应用引力加速度到火柴人
      const torsoBody = this.stickman.getBody().getBody('torso');
      if (torsoBody) {
        torsoBody.applyForceToCenter(Vec2(acceleration.x * 50, acceleration.y * 50));
      }
    }
  }

  /**
   * 更新相对论参数
   */
  private updateRelativityParameters(): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (!torso) return;

    const velocity = torso.getLinearVelocity();
    const velocityMagnitude = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);

    // 计算速度比率
    const normalizedVelocity = Math.min(velocityMagnitude / 100, 0.99);
    this.playerVelocityRatio = normalizedVelocity;

    // 计算洛伦兹因子
    try {
      this.playerGamma = this.relativity.calculateGamma(normalizedVelocity);
    } catch {
      this.playerGamma = Infinity;
    }

    // 更新UI
    this.velocityText.setText(`速度: ${this.playerVelocityRatio.toFixed(3)}c`);

    if (this.playerGamma > 100) {
      this.gammaText.setText(`γ: ∞ (接近光速!)`);
    } else {
      this.gammaText.setText(`γ: ${this.playerGamma.toFixed(2)}`);
    }

    this.timeDisplayText.setText(`旅程时间: ${this.elapsedTime.toFixed(1)}s`);
  }

  /**
   * 创建星星背景
   */
  private createStarBackground(): void {
    // 创建随机星星
    for (let i = 0; i < 200; i++) {
      this.stars.push({
        x: Math.random() * GAME_WIDTH,
        y: Math.random() * GAME_HEIGHT,
        brightness: Math.random(),
        size: 1 + Math.random() * 2,
        twinkleSpeed: 0.5 + Math.random() * 2,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  /**
   * 更新视觉效果
   */
  private updateVisualEffects(dt: number): void {
    // 更新事件视界脉冲
    this.eventHorizonPulse += dt * 2;

    // 更新黑洞渲染
    this.renderBlackhole();

    // 更新粒子
    this.updateParticles(dt);

    // 更新星星
    this.renderStars(dt);
  }

  /**
   * 渲染黑洞
   */
  private renderBlackhole(): void {
    this.blackholeGraphics.clear();

    // 中心黑洞（纯黑）
    const pulseFactor = 1 + Math.sin(this.eventHorizonPulse) * 0.1;
    const displayRadius = this.blackholeRadius * pulseFactor;

    // 外层吸积盘
    for (let i = 5; i >= 1; i--) {
      const ringRadius = displayRadius * (1 + i * 0.5);
      const alpha = 0.3 - i * 0.05;
      const hue = (this.currentStage - 1) * 30;

      this.blackholeGraphics.lineStyle(3 + i, Phaser.Display.Color.GetColor(100 + hue, 50 + hue, 150), alpha);
      this.blackholeGraphics.strokeCircle(
        this.blackholePosition.x,
        this.blackholePosition.y,
        ringRadius
      );
    }

    // 事件视界
    this.blackholeGraphics.fillStyle(0x000000, 1);
    this.blackholeGraphics.fillCircle(
      this.blackholePosition.x,
      this.blackholePosition.y,
      displayRadius
    );

    // 引力透镜效果（扭曲光线）
    this.renderGravitationalLensing();
  }

  /**
   * 渲染引力透镜效果
   */
  private renderGravitationalLensing(): void {
    // 从屏幕边缘发射光线
    const rayCount = 12;
    const rayColors = [0xff6b6b, 0x4ecdc4, 0xffd93d, 0x6bcb77];

    for (let i = 0; i < rayCount; i++) {
      const angle = (i / rayCount) * Math.PI * 2 + this.elapsedTime * 0.5;
      const startX = GAME_WIDTH / 2 + Math.cos(angle) * (GAME_WIDTH / 2 + 100);
      const startY = GAME_HEIGHT / 2 + Math.sin(angle) * (GAME_HEIGHT / 2 + 100);

      const direction = Vec2(
        this.blackholePosition.x - startX,
        this.blackholePosition.y - startY
      );
      const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
      const normalizedDir = Vec2(direction.x / dirLength, direction.y / dirLength);

      // 追踪光线
      const result = this.blackhole.traceLightRay(
        Vec2(startX, startY),
        normalizedDir,
        { stepSize: 10, maxSteps: 50, escapeDistance: 600 }
      );

      // 绘制光线路径
      if (result.path.length > 1) {
        const colorIndex = i % rayColors.length;
        const rayColor = rayColors[colorIndex] ?? 0xffffff;

        this.blackholeGraphics.lineStyle(2, rayColor, result.captured ? 0.8 : 0.4);
        this.blackholeGraphics.beginPath();

        const firstPoint = result.path[0];
        if (firstPoint) {
          this.blackholeGraphics.moveTo(firstPoint.x, firstPoint.y);
        }

        for (let j = 1; j < result.path.length; j++) {
          const point = result.path[j];
          if (point) {
            this.blackholeGraphics.lineTo(point.x, point.y);
          }
        }

        this.blackholeGraphics.strokePath();
      }
    }
  }

  /**
   * 更新粒子系统
   */
  private updateParticles(dt: number): void {
    this.particleGraphics.clear();

    // 更新现有粒子
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      if (!particle) continue;

      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.life -= dt;

      if (particle.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      const alpha = particle.life / particle.maxLife;
      this.particleGraphics.fillStyle(particle.color, alpha);
      this.particleGraphics.fillCircle(particle.x, particle.y, particle.size);
    }

    // 根据阶段生成新粒子
    if (this.gameState === 'running') {
      this.generateStageParticles();
    } else if (this.gameState === 'finale') {
      this.generateBigBangParticles();
    }
  }

  /**
   * 根据阶段生成粒子
   */
  private generateStageParticles(): void {
    const particleCount = this.currentStage === 4 ? 3 : 1;

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = this.blackholeRadius * (2 + Math.random() * 3);
      const x = this.blackholePosition.x + Math.cos(angle) * distance;
      const y = this.blackholePosition.y + Math.sin(angle) * distance;

      // 向黑洞中心移动
      const toCenter = Vec2(
        this.blackholePosition.x - x,
        this.blackholePosition.y - y
      );
      const speed = 20 + Math.random() * 30;

      this.particles.push({
        x,
        y,
        vx: (toCenter.x / Math.sqrt(toCenter.x * toCenter.x + toCenter.y * toCenter.y)) * speed,
        vy: (toCenter.y / Math.sqrt(toCenter.x * toCenter.x + toCenter.y * toCenter.y)) * speed,
        life: 2 + Math.random() * 2,
        maxLife: 4,
        color: STAGE_COLORS[this.currentStage],
        size: 2 + Math.random() * 3,
      });
    }
  }

  /**
   * 生成大爆炸粒子
   */
  private generateBigBangParticles(): void {
    const intensity = this.bigBangProgress * 5;

    for (let i = 0; i < intensity; i++) {
      const angle = Math.random() * Math.PI * 2;
      const x = GAME_WIDTH / 2;
      const y = GAME_HEIGHT / 2;
      const speed = 100 + Math.random() * 200 * this.bigBangProgress;

      // 大爆炸粒子颜色渐变
      const colors = [0xffffff, 0xffff00, 0xff8800, 0xff0000, 0xff00ff, 0x00ffff];
      const colorIndex = Math.floor(Math.random() * colors.length);
      const color = colors[colorIndex] ?? 0xffffff;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1 + Math.random() * 2,
        maxLife: 3,
        color,
        size: 3 + Math.random() * 5,
      });
    }
  }

  /**
   * 渲染星星
   */
  private renderStars(dt: number): void {
    this.starGraphics.clear();

    for (const star of this.stars) {
      star.twinklePhase += star.twinkleSpeed * dt;
      const brightness = star.brightness * (0.5 + 0.5 * Math.sin(star.twinklePhase));

      this.starGraphics.fillStyle(0xffffff, brightness);
      this.starGraphics.fillCircle(star.x, star.y, star.size);
    }
  }

  /**
   * 启动视觉效果更新循环
   */
  private startVisualEffects(): void {
    // 光线脉冲效果
    this.time.addEvent({
      delay: 100,
      callback: () => {
        this.updateLightSpeedPulse();
      },
      loop: true,
    });
  }

  /**
   * 更新光速脉冲效果
   */
  private updateLightSpeedPulse(): void {
    // 当速度接近光速时显示脉冲效果
    if (this.playerVelocityRatio > 0.5 && this.gameState === 'running') {
      const headPos = this.stickman.getHeadPosition();
      const pulseIntensity = (this.playerVelocityRatio - 0.5) / 0.5;
      const pulsePhase = Math.sin(this.elapsedTime * 8) * 0.5 + 0.5;

      // 速度光环
      this.blackholeGraphics.lineStyle(
        3 + pulseIntensity * 5,
        0xffffff,
        pulseIntensity * pulsePhase * 0.5
      );
      this.blackholeGraphics.strokeCircle(
        headPos.x,
        headPos.y,
        30 + pulseIntensity * 20 * pulsePhase
      );
    }
  }

  /**
   * 更新大爆炸动画
   */
  private updateBigBangAnimation(dt: number): void {
    this.bigBangProgress += dt * 0.1;

    switch (this.bigBangPhase) {
      case 'preparation':
        if (this.bigBangProgress > 1) {
          this.bigBangPhase = 'ignition';
          this.bigBangProgress = 0;
        }
        break;

      case 'ignition':
        if (this.bigBangProgress > 2) {
          this.bigBangPhase = 'expansion';
          this.bigBangProgress = 0;
          this.expansionRadius = 0;
        }
        break;

      case 'expansion':
        this.expansionRadius += dt * 300;
        if (this.bigBangProgress > 3) {
          this.bigBangPhase = 'cooling';
          this.bigBangProgress = 0;
        }
        break;

      case 'cooling':
        if (this.bigBangProgress > 2) {
          this.bigBangPhase = 'complete';
          this.showUniverseCreated();
        }
        break;
    }

    this.renderBigBang();
  }

  /**
   * 渲染大爆炸效果
   */
  private renderBigBang(): void {
    this.bigBangGraphics.clear();

    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;

    switch (this.bigBangPhase) {
      case 'preparation':
        // 能量聚集
        const prepRadius = 50 * this.bigBangProgress;
        this.bigBangGraphics.fillStyle(0xffffff, 0.8);
        this.bigBangGraphics.fillCircle(centerX, centerY, prepRadius);
        break;

      case 'ignition':
        // 点燃
        const igniteRadius = 50 + this.bigBangProgress * 100;
        const igniteAlpha = 0.5 + 0.5 * Math.sin(this.elapsedTime * 20);
        this.bigBangGraphics.fillStyle(0xffff00, igniteAlpha);
        this.bigBangGraphics.fillCircle(centerX, centerY, igniteRadius);
        this.bigBangGraphics.fillStyle(0xffffff, igniteAlpha * 0.5);
        this.bigBangGraphics.fillCircle(centerX, centerY, igniteRadius * 0.5);
        break;

      case 'expansion':
        // 膨胀
        this.bigBangGraphics.fillStyle(0xff4400, 0.3);
        this.bigBangGraphics.fillCircle(centerX, centerY, this.expansionRadius);
        this.bigBangGraphics.fillStyle(0xffff00, 0.5);
        this.bigBangGraphics.fillCircle(centerX, centerY, this.expansionRadius * 0.3);
        this.bigBangGraphics.fillStyle(0xffffff, 0.8);
        this.bigBangGraphics.fillCircle(centerX, centerY, this.expansionRadius * 0.1);
        break;

      case 'cooling':
        // 冷却形成星系
        const coolProgress = this.bigBangProgress / 2;
        const bgAlpha = Math.max(0, 0.3 - coolProgress * 0.3);
        this.bigBangGraphics.fillStyle(0xff4400, bgAlpha);
        this.bigBangGraphics.fillCircle(centerX, centerY, this.expansionRadius);
        break;
    }
  }

  /**
   * 显示宇宙创生完成
   */
  private showUniverseCreated(): void {
    if (this.universeCreated) return;
    this.universeCreated = true;

    // 创建新宇宙背景
    this.cameras.main.setBackgroundColor('#0a0a2e');

    // 重新生成星星
    this.stars = [];
    for (let i = 0; i < 300; i++) {
      this.stars.push({
        x: Math.random() * GAME_WIDTH,
        y: Math.random() * GAME_HEIGHT,
        brightness: 0.5 + Math.random() * 0.5,
        size: 1 + Math.random() * 2,
        twinkleSpeed: 0.3 + Math.random() * 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }

    // 解锁最终成就
    this.unlockAchievement('universe-creator');
  }

  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private createPlatforms(): void {
    const platforms = this.levelConfig?.platforms ?? [];
    const platformGraphics = this.add.graphics();

    for (const platform of platforms) {
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction: platform.friction ?? 0.5,
        restitution: platform.restitution ?? 0,
      });

      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      const color = platform.color ?? 0x4a4a6a;

      platformGraphics.fillStyle(color, 1);
      platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
    }
  }

  private createCheckpoints(): void {
    for (const checkpoint of this.checkpoints) {
      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const color = checkpoint.color ?? CHECKPOINT_COLOR;

      const graphics = this.add.graphics();
      graphics.fillStyle(color, 0.3);
      graphics.fillRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);
      graphics.lineStyle(3, color);
      graphics.strokeRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);

      this.checkpointGraphics.set(checkpoint.id, graphics);

      const stageLabel = this.stages.find(s => s.stage === checkpoint.stage);
      this.add.text(screenX + checkpoint.size.width / 2, screenY - 15, `阶段${checkpoint.stage}: ${stageLabel?.name ?? ''}`, {
        fontSize: '12px',
        color: '#ffa500',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createGoalZone(): void {
    const goal = this.levelConfig?.goal;
    if (!goal) return;

    const screenX = goal.position.x - goal.size.width / 2;
    const screenY = GAME_HEIGHT - goal.position.y - goal.size.height / 2;

    this.goalZone = new Phaser.Geom.Rectangle(
      screenX,
      screenY,
      goal.size.width,
      goal.size.height
    );

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(GOAL_COLOR, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, GOAL_COLOR);
    goalGraphics.strokeRectShape(this.goalZone);

    this.add.text(this.goalZone.centerX, this.goalZone.y - 15, '奇点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    const startY = playerConfig?.startPosition.y ?? 300;
    const startX = playerConfig?.startPosition.x ?? 100;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: this.baseWalkSpeed,
        jumpForce: 150,
        colors: {
          line: 0xffffff,
          headFill: 0x1a1a2e,
          lineWidth: 3,
        },
      },
      GAME_HEIGHT
    );
  }

  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '🌌 宇宙创生 - 终极挑战', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 阶段标题
    this.stageText = this.add.text(GAME_WIDTH / 2, 60, '', {
      fontSize: '18px',
      color: '#edc240',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 85, '', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 进度文本
    this.progressText = this.add.text(10, 10, '进度: 阶段1/5', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 速度显示
    this.velocityText = this.add.text(10, 30, '速度: 0.000c', {
      fontSize: '14px',
      color: '#edc240',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 伽玛因子显示
    this.gammaText = this.add.text(10, 50, 'γ: 1.00', {
      fontSize: '14px',
      color: '#28a745',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 时间显示
    this.timeDisplayText = this.add.text(10, 70, '旅程时间: 0.0s', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 公式展示
    this.formulaText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 30, '', {
      fontSize: '16px',
      color: '#00ff00',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5);

    this.createStageIndicators();
    this.updateStageDisplay();
  }

  private createStageIndicators(): void {
    const startX = GAME_WIDTH - 400;
    const y = 10;
    const width = 75;
    const gap = 5;

    for (let i = 1; i <= 5; i++) {
      const x = startX + (i - 1) * (width + gap);
      const stageConfig = this.stages.find(s => s.stage === i);

      const bg = this.add.graphics();
      bg.fillStyle(STAGE_COLORS[i as CurrentStage], 0.3);
      bg.fillRect(x, y, width, 35);
      bg.lineStyle(2, STAGE_COLORS[i as CurrentStage]);
      bg.strokeRect(x, y, width, 35);

      this.add.text(x + width / 2, y + 17, stageConfig?.name ?? `阶段${i}`, {
        fontSize: '9px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  /**
   * 创建成就系统
   */
  private createAchievements(): void {
    this.achievements = [
      {
        id: 'blackhole-survivor',
        name: '黑洞幸存者',
        description: '完成黑洞引力阶段',
        unlocked: false,
      },
      {
        id: 'event-horizon-crosser',
        name: '事件视界穿越者',
        description: '穿越事件视界',
        unlocked: false,
      },
      {
        id: 'singularity-reacher',
        name: '奇点探索者',
        description: '到达黑洞奇点',
        unlocked: false,
      },
      {
        id: 'big-bang-witness',
        name: '大爆炸见证者',
        description: '见证宇宙大爆炸',
        unlocked: false,
      },
      {
        id: 'universe-creator',
        name: '宇宙创造者',
        description: '创生新宇宙',
        unlocked: false,
      },
      {
        id: 'physics-master',
        name: '物理大师',
        description: '完成所有20关',
        unlocked: false,
      },
    ];
  }

  /**
   * 解锁成就
   */
  private unlockAchievement(id: string): void {
    const achievement = this.achievements.find(a => a.id === id);
    if (achievement && !achievement.unlocked) {
      achievement.unlocked = true;
      this.gameStats.achievementsUnlocked++;

      // 显示成就通知
      this.showAchievementNotification(achievement);
    }
  }

  /**
   * 显示成就通知
   */
  private showAchievementNotification(achievement: { name: string; description: string }): void {
    const notification = this.add.container(GAME_WIDTH / 2, 120);

    const bg = this.add.graphics();
    bg.fillStyle(0x4ade80, 0.9);
    bg.fillRoundedRect(-150, -25, 300, 50, 10);

    const text = this.add.text(0, 0, `🏆 ${achievement.name}`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    notification.add([bg, text]);
    notification.setDepth(100);

    // 3秒后消失
    this.time.delayedCall(3000, () => {
      notification.destroy();
    });
  }

  /**
   * 加载游戏统计
   */
  private loadGameStats(): void {
    const gameState = getGameState();
    this.gameStats.totalLevelsCompleted = gameState.getCompletedCount();
  }

  private setupInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        this.resetLevel();
      }
    });
  }

  private checkCheckpointCollisions(): void {
    const headPos = this.stickman.getHeadPosition();

    for (const checkpoint of this.checkpoints) {
      if (this.completedStages.has(checkpoint.stage ?? 0)) continue;

      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const checkpointRect = new Phaser.Geom.Rectangle(
        screenX,
        screenY,
        checkpoint.size.width,
        checkpoint.size.height
      );

      if (checkpointRect.contains(headPos.x, headPos.y)) {
        this.completeStage(checkpoint.stage ?? 1);
      }
    }
  }

  private completeStage(stage: number): void {
    if (this.completedStages.has(stage)) return;

    this.completedStages.add(stage);

    // 解锁对应成就
    const achievementMap: Record<number, string> = {
      1: 'blackhole-survivor',
      2: 'event-horizon-crosser',
      3: 'singularity-reacher',
      4: 'big-bang-witness',
    };

    const achievementId = achievementMap[stage];
    if (achievementId) {
      this.unlockAchievement(achievementId);
    }

    const graphics = this.checkpointGraphics.get(`checkpoint-${stage}`);
    if (graphics) {
      graphics.clear();
      graphics.fillStyle(0x4ade80, 0.5);
      graphics.fillRect(
        graphics.x ?? 0,
        graphics.y ?? 0,
        this.checkpoints.find(c => c.stage === stage)?.size.width ?? 60,
        this.checkpoints.find(c => c.stage === stage)?.size.height ?? 80
      );
    }

    this.showStageTransition(stage);

    if (stage < 5) {
      this.currentStage = (stage + 1) as CurrentStage;
    } else {
      // 完成所有阶段，触发大爆炸
      this.startBigBangFinale();
    }
  }

  /**
   * 开始大爆炸终局
   */
  private startBigBangFinale(): void {
    this.gameState = 'finale';
    this.bigBangPhase = 'preparation';
    this.bigBangProgress = 0;
  }

  private showStageTransition(stage: number): void {
    const stageConfig = this.stages.find(s => s.stage === stage);
    const transitionBg = this.add.graphics();
    transitionBg.fillStyle(0x000000, 0.7);
    transitionBg.fillRect(GAME_WIDTH / 2 - 150, GAME_HEIGHT / 2 - 60, 300, 120);

    const stageColor = STAGE_COLORS[stage as CurrentStage];
    const colorHex = `#${stageColor.toString(16).padStart(6, '0')}`;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, `阶段${stage} 完成！`, {
      fontSize: '24px',
      color: colorHex,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    if (stageConfig) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, stageConfig.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    this.time.delayedCall(1500, () => {
      transitionBg.destroy();
    });
  }

  private updateStageDisplay(): void {
    const stageConfig = this.stages.find(s => s.stage === this.currentStage);
    if (stageConfig && this.gameState === 'running') {
      this.stageText.setText(`当前: ${stageConfig.name}`);
      const stageColor = STAGE_COLORS[this.currentStage];
      this.stageText.setColor(`#${stageColor.toString(16).padStart(6, '0')}`);
      this.hintText.setText(stageConfig.hint ?? '');
    }
    this.progressText.setText(`进度: 阶段${this.currentStage}/5 (${this.completedStages.size}/5 完成)`);
    this.updateFormulaDisplay();
  }

  private updateFormulaDisplay(): void {
    let formula = '';
    switch (this.currentStage) {
      case 1:
        formula = '史瓦西半径: Rs = 2GM/c² | 引力加速度: a = GM/r²';
        break;
      case 2:
        formula = '事件视界: r = Rs | 时间膨胀: t\' = t/√(1-Rs/r)';
        break;
      case 3:
        formula = '奇点密度: ρ → ∞ | 引力红移: z = 1/√(1-Rs/r) - 1';
        break;
      case 4:
        formula = '大爆炸理论: t ≈ 13.8亿年 | 宇宙膨胀: H₀ ≈ 70 km/s/Mpc';
        break;
      case 5:
        formula = '新宇宙创生 | 恭喜完成终极挑战！';
        break;
    }
    this.formulaText.setText(formula);
  }

  private checkVictory(): void {
    const headPos = this.stickman.getHeadPosition();

    // 检查是否到达终点（奇点）
    if (this.goalZone.contains(headPos.x, headPos.y) && this.completedStages.size >= 4) {
      this.isVictory = true;
      this.gameState = 'success';
      this.showVictoryMessage();
    }
  }

  private showVictoryMessage(): void {
    const saveGameState = getGameState();
    saveGameState.completeLevel(20, this.elapsedTime);

    // 解锁最终成就
    this.unlockAchievement('physics-master');

    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.85);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // 主标题
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 150, '🎉 宇宙创生完成！', {
      fontSize: '36px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 100,
      '你成功创生了一个新宇宙！',
      {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    // 统计总结
    const statsText = `
═══════════════════════════════
         🌟 游戏统计 🌟
═══════════════════════════════
  通关关卡: ${this.gameStats.totalLevelsCompleted + 1}/20
  本关用时: ${this.elapsedTime.toFixed(1)} 秒
  解锁成就: ${this.gameStats.achievementsUnlocked}/${this.achievements.length}
═══════════════════════════════

你已掌握了:
  • 经典力学 (速度、加速度、抛物线)
  • 电磁学 (电场、磁场、电磁感应)
  • 狭义相对论 (时间膨胀、长度收缩)
  • 广义相对论 (黑洞、引力透镜)
  • 宇宙学 (大爆炸、宇宙创生)

物理学之旅圆满完成！
    `;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, statsText, {
      fontSize: '14px',
      color: '#edc240',
      fontFamily: 'Arial',
      align: 'center',
      lineSpacing: 5,
    }).setOrigin(0.5);

    // 按钮
    const backBtn = this.add.text(GAME_WIDTH / 2 - 100, GAME_HEIGHT / 2 + 180, '返回', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const creditsBtn = this.add.text(GAME_WIDTH / 2 + 100, GAME_HEIGHT / 2 + 180, '制作名单', {
      fontSize: '20px',
      color: '#ffd700',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    creditsBtn.on('pointerdown', () => {
      this.showCredits();
    });
  }

  /**
   * 显示制作名单
   */
  private showCredits(): void {
    const creditsBg = this.add.graphics();
    creditsBg.fillStyle(0x000000, 0.95);
    creditsBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    const creditsText = `
════════════════════════════════════════
            物理探索游戏
         Physics Exploration Game
════════════════════════════════════════

              🎮 游戏开发
           OpenCode AI Assistant

              📚 物理顾问
           基于真实物理定律

              🎨 视觉设计
           火柴人风格 + 科幻主题

              🎵 游戏引擎
           Phaser 3 + Planck.js

════════════════════════════════════════

        感谢你的探索之旅！
       从基础物理到宇宙创生
         你已完成全部20关

           祝你好运！🚀

════════════════════════════════════════
    `;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, creditsText, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
      align: 'center',
      lineSpacing: 8,
    }).setOrigin(0.5);

    const closeBtn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 50, '关闭', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  private resetLevel(): void {
    this.scene.restart();
  }

  // 公开访问方法
  getCurrentStage(): CurrentStage {
    return this.currentStage;
  }

  getCompletedStages(): Set<number> {
    return this.completedStages;
  }

  getIsVictory(): boolean {
    return this.isVictory;
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getGameState(): GameState {
    return this.gameState;
  }

  getBlackhole(): Blackhole {
    return this.blackhole;
  }

  getRelativity(): Relativity {
    return this.relativity;
  }

  getPlayerVelocityRatio(): number {
    return this.playerVelocityRatio;
  }

  getPlayerGamma(): number {
    return this.playerGamma;
  }

  getBigBangPhase(): BigBangPhase {
    return this.bigBangPhase;
  }

  getAchievements(): Array<{ id: string; name: string; description: string; unlocked: boolean }> {
    return [...this.achievements];
  }
}